import { useDeferredValue, useEffect, useRef, useState } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import { numberFormatter } from "@common/helpers/number";
import { pluralize } from "@common/helpers/string";
import { SearchIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { useDataGridContext } from "../DataGridContext";
import type { GridController } from "../focus/gridController";
import type { DataGridInstance } from "../types";
import styles from "./toolbar.module.scss";

interface DataGridSearchProps {
    placeholder?: string;
    className?: string;
}

interface SearchFieldProps<TData extends RowData> extends DataGridSearchProps {
    instance: DataGridInstance<TData>;
    controller: GridController;
}

const toFilterText = (value: string | number | undefined | null) =>
    value === undefined || value === null ? "" : String(value);

const SearchField = <TData extends RowData>({
    instance,
    controller,
    placeholder = "Search",
    className,
}: Readonly<SearchFieldProps<TData>>) => {
    const { table } = instance;
    const filter = useSelector(table.atoms.globalFilter, toFilterText);
    const [value, setValue] = useState(filter);
    const [syncedFilter, setSyncedFilter] = useState(filter);
    const deferredValue = useDeferredValue(value);
    const inputRef = useRef<HTMLInputElement>(null);

    if (filter !== syncedFilter) {
        setSyncedFilter(filter);
        if (filter !== deferredValue) {
            setValue(filter);
        }
    }

    useEffect(() => {
        if (deferredValue !== toFilterText(table.atoms.globalFilter.get())) {
            table.setGlobalFilter(deferredValue);
        }
    }, [deferredValue, table]);

    useEffect(() => {
        const focusSearch = () => inputRef.current?.focus();
        controller.setFocusSearch(focusSearch);
        return () => {
            if (controller.focusSearch === focusSearch) {
                controller.setFocusSearch(null);
            }
        };
    }, [controller]);

    const resultCount = instance.totalRowCount;

    return (
        <HStack
            gap={8}
            align="center"
            className={classNames(styles.search, className)}
        >
            <Input
                ref={inputRef}
                type="search"
                aria-label={placeholder}
                placeholder={placeholder}
                leftIcon={<SearchIcon />}
                value={value}
                onValueChange={setValue}
                clearable
                onClear={() => {
                    setValue("");
                    table.setGlobalFilter("");
                }}
                data-testid="data-grid-search"
            />
            {filter === "" ? null : (
                <span className={styles.resultCount} aria-live="polite">
                    {numberFormatter.format(resultCount)}{" "}
                    {pluralize("result", resultCount)}
                </span>
            )}
        </HStack>
    );
};

/**
 * Search box for the table's global filter. Typing stays responsive: the
 * filter follows a deferred copy of the text. Shows the result count while
 * a search is set; `/` in the table focuses it. Cells highlight matches
 * through `HighlightedText`.
 */
export const DataGridSearch = (props: Readonly<DataGridSearchProps>) => {
    const { visit, controller } = useDataGridContext();
    return visit((instance) => (
        <SearchField {...props} instance={instance} controller={controller} />
    ));
};
